import { Fragment } from 'react';
import { FiStar } from 'react-icons/fi';
import { useCurrency } from '../../context/CurrencyContext';
import ProductDescription from './ProductDescription';

const ProductInfo = ({ book, isBook, avgRating, reviewCount, categoryName }) => {
  const { formatBookPrice, formatCurrencyAmount, getBookAmount, getBookDiscount } = useCurrency();
  const ratingsLabel = (book.reviewCount || reviewCount).toLocaleString('en-IN');
  const savings = getBookAmount(book, 'originalPrice') - getBookAmount(book);
  const marketDiscount = getBookDiscount(book);
  const subtitle = String(book.subtitle ?? '').trim();

  return (
    <section className="pb-3">
      <div className="flex flex-wrap items-baseline gap-x-2">
        <h1 className="font-serif text-xl md:text-2xl font-normal text-red-600 leading-snug">
          {book.title}
        </h1>
      </div>
      {subtitle && <p className="mt-2 text-sm leading-relaxed text-gray-700 break-words">{subtitle}</p>}

      {(book.author || book.publisher) && (
        <p className="text-xs text-black mt-2">
          {book.author && (
            <>By: <span className="text-cyan-700">{book.author}</span> (Author)</>
          )}
          {book.publisher && <><span className="mx-2">|</span>Publisher: <span className="text-cyan-700">{book.publisher}</span></>}
        </p>
      )}

      <div className="flex items-center gap-1.5 mt-2 text-sm">
        <span className="text-black">{avgRating}</span>
        <span className="flex items-center gap-0.5 text-orange-500">
          {[...Array(5)].map((_, i) => (
            <FiStar
              key={i}
              className={i < Math.round(Number(avgRating)) ? 'fill-orange-500' : 'text-black'}
            />
          ))}
        </span>
        <span className="mx-1 text-black">|</span>
        <a href="#reviews" className="text-black hover:text-red-600 hover:underline">
          {ratingsLabel} Reviews
        </a>
      </div>

      {book.isBestseller && book.bestsellerRank > 0 && (
        <p className="text-xs text-black mt-1">
          <span className="bg-orange-600 text-white px-1.5 py-0.5 text-[10px] font-bold rounded mr-1">
            #{book.bestsellerRank} Best Seller
          </span>
          in {categoryName}
        </p>
      )}

      <div className="mt-2 text-sm">
        <p className="text-lg text-red-600">{formatBookPrice(book)}</p>
        {getBookAmount(book, 'originalPrice') > getBookAmount(book) && (
          <p className="text-black">
            M.R.P.: <span className="line-through">{formatBookPrice(book, 'originalPrice')}</span>
          </p>
        )}
        {savings > 0 && (
          <p className="text-black">
            Save: {formatCurrencyAmount(savings)}{marketDiscount > 0 && ` (${marketDiscount}%)`}
          </p>
        )}
      </div>

      <div className="mt-3 text-sm leading-snug">
        <p className={book.stock > 0 ? 'text-green-600' : 'text-red-600'}>
          {book.stock > 0 ? 'Available' : 'Coming soon'}
        </p>
      </div>

      {isBook && book.description?.trim() && (
        <section className="mt-4 border-t border-gray-300 py-4 px-2">
          <h2 className="text-xl font-bold text-red-600 mb-2">About the Book</h2>
          <ProductDescription text={book.description} collapsible previewLength={180} />
        </section>
      )}

      {!isBook && book.specs && Object.keys(book.specs).length > 0 && (
        <div className="mt-4">
          <dl className="grid grid-cols-[140px_1fr] gap-y-1.5 text-xs text-black max-w-md">
            {Object.entries(book.specs).map(([k, v]) => (
              <Fragment key={k}>
                <dt className="font-bold text-black">{k}</dt>
                <dd className="text-black">{String(v)}</dd>
              </Fragment>
            ))}
          </dl>
        </div>
      )}
    </section>
  );
};

export default ProductInfo;
